import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { dial, isDialable, formatPhoneNumber } from "../helpers/telephony";
import { openCallPanel } from "./telephony/CallPanel";
import { loadCompany } from "./ActivityCompanyModal";

/**
 * Wen rufe ich an — bzw. wer hat angerufen? Nummer der Firma oder eines Kontakts
 * auswaehlen; ausgehend wird dabei sofort gewaehlt. Danach uebernimmt das
 * schwebende `CallPanel` das Protokoll, damit die Seite bedienbar bleibt.
 *
 * Der Regelfall ist der ausgehende Anruf, deshalb ist er der Weg durch dieses
 * Fenster. Ein eingehender wird ueber den Knopf im Fuss festgehalten: er waehlt
 * nicht, sondern oeffnet direkt das Protokoll — wer angerufen hat, wird dort
 * ausgewaehlt (`CallOutcomePanel`), denn nur der Anrufer selbst weiss es.
 */
const CallContactSelectModal = (props) => {
  const {
    company: passedCompany,
    contacts = [],
    objectId,
    objectType,
    task,
    // Vorbelegung der Suche (Work Session: der Kontakt der Aufgabe). Leert der Nutzer
    // das Feld, greift der normale Filter und alle Kontakte stehen wieder da.
    defaultSearch = "",
    // Gesetzt, wo das Fenster ueber den Plus-Knopf entstand: schliesst es und
    // oeffnet die Firmenauswahl, um den Anruf bei einer anderen Firma zu beginnen.
    onChangeCompany,
    onClose,
    onSaved,
  } = props;
  const { t } = useTranslation(["components", "common", "customers"]);

  const [searchQuery, setSearchQuery] = useState(defaultSearch);
  const [customNumber, setCustomNumber] = useState("");

  // Die Firmenauswahl reicht nur `{ id, name }` herein — ohne Nachladen fehlte die
  // doNotContact-Sperre (und die Zentrale). Wo sie schon mitkommt, wird nichts geladen.
  const [loadedCompany, setLoadedCompany] = useState(null);
  const needsLoad = Boolean(passedCompany?.id) && passedCompany.doNotContact === undefined;
  useEffect(() => {
    if (!needsLoad) return;
    let cancelled = false;
    loadCompany(passedCompany.id)
      .then((c) => !cancelled && setLoadedCompany(c))
      .catch((err) => console.error("CallContactSelectModal: company load failed", err));
    return () => {
      cancelled = true;
    };
  }, [needsLoad, passedCompany?.id]);
  const company = loadedCompany ? { ...passedCompany, ...loadedCompany } : passedCompany;
  // Wie am Anrufknopf und im Mail-Fenster: eine gesperrte Firma WARNT, sie sperrt nicht.
  const blocked = company?.doNotContact === "true";

  // Uebergabe an das schwebende Protokollfenster. `manual` heisst: die Nummer stand
  // nirgends im Datensatz, also bietet das Protokoll das Hinterlegen an — bei einem
  // eingehenden Anruf ist das die Regel und nicht die Ausnahme.
  const openLog = (number, contact = null, manual = false, direction = "outbound") => {
    openCallPanel({
      direction,
      dialResult: { number, dialedAt: new Date().toISOString() },
      company,
      contacts,
      companyID: company?.id,
      contactPersonId: contact?.id,
      unknownNumber: manual || direction === "inbound" ? number : "",
      objectId,
      objectType,
      task,
      onSaved,
    });
    if (onClose) onClose();
  };

  const handleDial = (number, contact = null, manual = false) => {
    if (!number || !isDialable(number)) {
      toast.error(t("Invalid phone number"));
      return;
    }

    const result = dial(number);
    console.log("[CallContactSelectModal dial]", result);
    if (result.launched) {
      toast.success(t("Calling {{number}}…", { number: formatPhoneNumber(result.number) }));
    } else {
      toast.error(t("Could not start the call."));
    }

    openLog(result.number, contact, manual);
  };

  // Ein Knopf je Nummer. Die Art steht als Text dabei, nicht nur als Icon: wer
  // waehrend des Telefonierens hinschaut, soll nicht raten muessen, ob er gerade
  // das Festnetz oder das Handy anruft.
  const dialButton = ({ number, kind, contact = null, variant }) => (
    <Button
      variant={variant}
      size="sm"
      className="d-inline-flex align-items-center gap-1"
      onClick={() => handleDial(number, contact)}
      title={kind === "mobil" ? t("Call mobile") : t("Call phone")}
    >
      <i className={kind === "mobil" ? "mdi mdi-cellphone" : "mdi mdi-phone-outline"} />
      <span>{formatPhoneNumber(number)}</span>
      <span className="opacity-75">
        · {kind === "mobil" ? t("Mobile") : t("Landline")}
      </span>
    </Button>
  );

  const handleRequestClose = () => {
    if (onClose) onClose();
  };

  // Filter contacts by search query
  const query = searchQuery.trim().toLowerCase();
  const filteredContacts = contacts.filter((c) => {
    if (!query) return true;
    const name = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
    const phone = (c.phone || "").toLowerCase();
    const mobil = (c.mobil || "").toLowerCase();
    const email = (c.email || "").toLowerCase();
    const position = (c.position || "").toLowerCase();
    return (
      name.includes(query) ||
      phone.includes(query) ||
      mobil.includes(query) ||
      email.includes(query) ||
      position.includes(query)
    );
  });

  // Companies hat im Schema nur `phone` — kein Mobilfeld.
  const companyHasPhone = Boolean(company?.phone);

  return (
    <Modal
      show
      onHide={handleRequestClose}
      dialogClassName="modal-dialog-centered"
      size="xl"
    >
      <Modal.Header onHide={handleRequestClose} closeButton>
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#dcfce7",
              color: "#16a34a",
            }}
          >
            <i className="mdi mdi-phone-outline" style={{ fontSize: "18px" }} />
          </div>
          <h4 className="modal-title mb-0">{t("Start a call")}</h4>
        </div>
      </Modal.Header>

      <Modal.Body className="p-3">
        <div>
            {blocked && (
              <Alert variant="danger" className="mb-3">
                <i className="mdi mdi-alert-outline me-1" />
                {t("Company marked as 'do not contact'.")}
              </Alert>
            )}

            {/* Search Input */}
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0">
                  <i className="mdi mdi-magnify text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t("Search contact, name or number…")}
                  className="border-start-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <Button
                    variant="light"
                    className="border"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="mdi mdi-close" />
                  </Button>
                )}
              </InputGroup>
            </div>

            {/* Section 1: Company Phone */}
            {companyHasPhone && (!query || company?.name?.toLowerCase().includes(query) || (company?.phone || "").includes(query)) && (
              <div className="mb-3">
                <div className="text-muted font-12 text-uppercase fw-bold mb-2">
                  {t("Company number")}
                </div>
                <Card className="border shadow-none mb-0 bg-light">
                  <Card.Body className="p-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                      <div
                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          backgroundColor: "#e2e8f0",
                          color: "#475569",
                        }}
                      >
                        <i className="mdi mdi-domain" style={{ fontSize: "18px" }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-semibold text-dark text-truncate">
                          {company?.name || t("Headquarters")}
                        </div>
                        <div className="text-muted small">
                          {t("Main office")}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {company?.phone &&
                        isDialable(company.phone) &&
                        dialButton({
                          number: company.phone,
                          kind: "phone",
                          variant: "success",
                        })}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Section 2: Contact Persons */}
            <div className="mb-3">
              <div className="text-muted font-12 text-uppercase fw-bold mb-2">
                {t("Contacts")} ({filteredContacts.length})
              </div>

              {filteredContacts.length === 0 ? (
                <div className="text-center text-muted py-3 border rounded bg-light">
                  <i className="mdi mdi-account-off-outline font-24 d-block mb-1 text-muted" />
                  <span className="small">{t("No contacts found.")}</span>
                </div>
              ) : (
                /* Die Liste scrollt erst, wenn der Bildschirm nicht reicht —
                   darum die Fensterhoehe als Grenze und keine feste Zahl. */
                <div
                  className="d-flex flex-column gap-2"
                  style={{ maxHeight: "calc(100vh - 26rem)", overflowY: "auto" }}
                >
                  {filteredContacts.map((contact) => {
                    const fullName = [contact.firstName, contact.lastName]
                      .filter(Boolean)
                      .join(" ");
                    const hasPhone = contact.phone && isDialable(contact.phone);
                    const hasMobile = contact.mobil && isDialable(contact.mobil);

                    return (
                      <Card key={contact.id} className="border shadow-none mb-0">
                        <Card.Body className="p-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                            <div
                              className="d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                backgroundColor: "#f1f5f9",
                                color: "#334155",
                                fontWeight: 600,
                                fontSize: "14px",
                              }}
                            >
                              {contact.firstName ? contact.firstName[0].toUpperCase() : <i className="mdi mdi-account" />}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className="fw-semibold text-dark text-truncate">
                                {fullName || t("Unnamed Contact")}
                              </div>
                              <div className="text-muted small text-truncate">
                                {[contact.position, contact.email]
                                  .filter(Boolean)
                                  .join(" · ") || t("Contact person")}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {hasPhone &&
                              dialButton({
                                number: contact.phone,
                                kind: "phone",
                                contact,
                                variant: "success",
                              })}
                            {hasMobile &&
                              dialButton({
                                number: contact.mobil,
                                kind: "mobil",
                                contact,
                                variant: hasPhone ? "outline-success" : "success",
                              })}
                            {!hasPhone && !hasMobile && (
                              <span className="text-muted small fst-italic">
                                {t("No phone number")}
                              </span>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Section 3: Manual Number Dial */}
            <div className="pt-2 border-top">
              <div className="text-muted font-12 text-uppercase fw-bold mb-2">
                {t("Or enter phone number directly")}
              </div>
              <Row className="g-2">
                <Col>
                  <Form.Control
                    type="tel"
                    placeholder={t("e.g. +49 170 1234567")}
                    value={customNumber}
                    onChange={(e) => setCustomNumber(e.target.value)}
                    // ponytail: gesetzt wird beim Verlassen des Feldes, nicht bei
                    // jedem Tastendruck — sonst springt der Cursor, sobald man
                    // mitten in der Nummer korrigiert. Gewaehlt wird ohnehin die
                    // bereinigte Form (dial() -> sanitizeNumber).
                    onBlur={() => setCustomNumber(formatPhoneNumber(customNumber))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isDialable(customNumber)) {
                        e.preventDefault();
                        handleDial(customNumber, null, true);
                      }
                    }}
                  />
                </Col>
                <Col xs="auto">
                  <Button
                    variant="success"
                    disabled={!isDialable(customNumber)}
                    onClick={() => handleDial(customNumber, null, true)}
                    className="d-inline-flex align-items-center gap-1"
                  >
                    <i className="mdi mdi-phone-outline" />
                    {t("Call")}
                  </Button>
                </Col>
              </Row>
            </div>
        </div>
      </Modal.Body>

      {/* Ein Anruf, der schon laeuft oder schon vorbei ist: hier wird nicht
          gewaehlt, sondern nur festgehalten. Steht eine Nummer im Feld oben,
          kommt sie mit; wer dran war, wird im Protokoll ausgewaehlt. Ausgehend
          gilt das genauso — z.B. nach einem Anruf ueber das Diensthandy statt
          ueber den Waehl-Knopf hier oben. */}
      <Modal.Footer className="justify-content-between">
        {onChangeCompany ? (
          <Button
            variant="light"
            className="d-inline-flex align-items-center gap-1"
            onClick={onChangeCompany}
          >
            <i className="uil-suitcase" />
            {t("Choose a different company")}
          </Button>
        ) : (
          <span />
        )}
        <span className="d-inline-flex gap-2">
          <Button
            variant="light"
            className="d-inline-flex align-items-center gap-1"
            onClick={() => openLog(customNumber, null, true, "outbound")}
          >
            <i className="mdi mdi-phone-outgoing" />
            {t("Log outgoing call")}
          </Button>
          <Button
            variant="light"
            className="d-inline-flex align-items-center gap-1"
            onClick={() => openLog(customNumber, null, true, "inbound")}
          >
            <i className="mdi mdi-phone-incoming" />
            {t("Log incoming call")}
          </Button>
        </span>
      </Modal.Footer>
    </Modal>
  );
};

export default CallContactSelectModal;
